import { useCallback, useState } from "react";
import { Search, X } from "lucide-react";

/**
 * Which category groups are open. While a search is active every group is
 * shown open, so matches are never hidden inside a collapsed section.
 */
export function useExpandedGroups(searching: boolean) {
  const [open, setOpen] = useState<Set<string>>(() => new Set());

  const isExpanded = useCallback(
    (category: string) => searching || open.has(category),
    [open, searching],
  );
  const toggle = useCallback((category: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);
  const expandAll = useCallback((categories: string[]) => setOpen(new Set(categories)), []);
  const collapseAll = useCallback(() => setOpen(new Set()), []);

  return { isExpanded, toggle, expandAll, collapseAll };
}

export function SearchInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-72">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-tribal-muted" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full rounded-lg border border-tribal-border bg-tribal-bg py-2 pl-8 pr-8 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-tribal-muted hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function ListToolbar({
  search,
  onSearch,
  placeholder,
  showArchived,
  onShowArchived,
  onExpandAll,
  onCollapseAll,
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder: string;
  showArchived: boolean;
  onShowArchived: (value: boolean) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <SearchInput value={search} onChange={onSearch} placeholder={placeholder} />
      <label className="flex items-center gap-2 text-sm text-tribal-muted">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => onShowArchived(e.target.checked)}
        />
        Show archived
      </label>
      <div className="ml-auto flex gap-2 text-xs">
        <button
          type="button"
          onClick={onExpandAll}
          className="rounded bg-white/5 px-2 py-1 text-tribal-muted hover:text-white"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={onCollapseAll}
          className="rounded bg-white/5 px-2 py-1 text-tribal-muted hover:text-white"
        >
          Collapse all
        </button>
      </div>
    </div>
  );
}

/** Archive / Restore button for a list row. */
export function ArchiveButton({
  isActive,
  pending,
  onClick,
}: {
  isActive: boolean;
  pending: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      className="rounded bg-white/5 px-2 py-1 text-xs text-tribal-muted hover:text-white disabled:opacity-50"
    >
      {isActive ? "Archive" : "Restore"}
    </button>
  );
}

export function ArchivedBadge() {
  return (
    <span className="ml-2 rounded bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-tribal-muted">
      Archived
    </span>
  );
}
